import React, { useState } from "react";
import {
  Menu,
  MenuItem,
  IconButton,
  Tooltip,
  Box,
  Typography,
  Avatar,
  Divider,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import accountIcon from "../assets/Sidebaricons/account.svg";
import accountIconActive from "../assets/Sidebaricons/accountroxo.svg";
import { useProfile } from "../hooks";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { getLoggedinUser } from "../api/apiCore";
import { useRedux } from "../hooks/index";
import { logoutUser } from "../redux/actions";
import { useGlobalTheme } from "../context/GlobalThemeContext";

interface ProfileDropdownMenuProps {
  onSideMenuClose?: () => void;
}

const ProfileDropdownMenu: React.FC<ProfileDropdownMenuProps> = ({
  onSideMenuClose,
}) => {
  const { t } = useTranslation("translation");
  const navigate = useNavigate();
  const { userProfile } = useProfile();
  const { dispatch } = useRedux();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const isMenuOpen = Boolean(anchorEl);
  const currentIcon = isMenuOpen ? accountIconActive : accountIcon;

  const handleClick = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };
  const handleClose = () => setAnchorEl(null);

  const userName = userProfile?.userName ?? "";
  const userInitial = userName.charAt(0).toUpperCase();

  const currentUser = getLoggedinUser();
  const isAdmin = currentUser?.role === "ADMIN";

  const theme = useTheme();
  // isDesktop means "at least 1024px", so "not isDesktop" means <= 1023px.
  const isDesktop = useMediaQuery("(min-width:1024px)");
  const isDark = useGlobalTheme();

  const isLightMode = theme.palette.mode === "light";
  const ICON_COLOR = isLightMode ? "#1a5f3a" : "white";

  return (
    <>
      <Tooltip title={t("profileTooltip")} placement="right" arrow>
        <Box
          onClick={handleClick}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            borderRadius: "0.5rem",
            backgroundColor: isMenuOpen ? "#FFFFFF" : "transparent",
            padding: "0.25rem",
            cursor: "pointer",
            transition: "all 0.2s ease",
            "&:hover": {
              backgroundColor: isMenuOpen
                ? "#FFFFFF"
                : "rgba(255, 255, 255, 0.1)",
            },
          }}
        >
          <IconButton
            disableRipple
            sx={{
              padding: 0,
              width: "clamp(40px, 6vh, 52px)",
              height: "clamp(40px, 6vh, 52px)",
              "&:hover": {
                backgroundColor: "transparent",
              },
            }}
          >
            <img
              src={currentIcon}
              alt="Account"
              style={{
                width: "clamp(24px, 4.2vh, 34px)",
                height: "clamp(24px, 4.2vh, 34px)",
                objectFit: "contain",
              }}
            />
          </IconButton>
          {!isDesktop && (
            <span style={{ color: ICON_COLOR, fontWeight: 500 }}>
              {t("profileTooltip")}
            </span>
          )}
        </Box>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          sx: {
            borderRadius: 2,
            minWidth: 220,
            ...(isDark && {
              bgcolor: "rgba(20, 20, 20, 0.98)",
              border: "1px solid rgba(255,255,255,0.08)",
              color: "#fff",
            }),
          },
        }}
        sx={{
          zIndex: 9999,
        }}
      >
        {/* Ver perfil */}
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            px: 2,
            py: 1.5,
            borderBottom: 1,
            borderColor: isDark ? "rgba(255,255,255,0.08)" : "divider",
            bgcolor: isDark ? "rgba(30,30,30,0.98)" : "background.paper",
          }}
        >
          <Avatar sx={{ bgcolor: "primary.main" }}>{userInitial}</Avatar>
          <Box sx={{ ml: 2 }}>
            <Typography
              variant="subtitle1"
              fontWeight="bold"
              sx={isDark ? { color: "#fff" } : undefined}
            >
              {userName}
            </Typography>
          </Box>
        </Box>

        {/* Itens principais */}
        <MenuItem
          onClick={() => {
            navigate("/settings/profile");
            handleClose();
            onSideMenuClose?.();
          }}
          sx={
            isDark
              ? {
                  color: "#fff",
                  "&:hover": { bgcolor: "rgba(255,255,255,0.08)" },
                }
              : undefined
          }
        >
          {t("dropdown.settings")}
        </MenuItem>

        <MenuItem
          onClick={() => {
            navigate("/settings/team");
            handleClose();
            onSideMenuClose?.();
          }}
          sx={
            isDark
              ? {
                  color: "#fff",
                  "&:hover": { bgcolor: "rgba(255,255,255,0.08)" },
                }
              : undefined
          }
        >
          {isAdmin ? t("dropdown.manageTeam") : t("dropdown.team")}
        </MenuItem>

        {/* Separator before end items */}
        <Divider
          sx={{
            my: 1,
            ...(isDark && { borderColor: "rgba(255,255,255,0.08)" }),
          }}
        />

        {/* Password recovery and logout items */}
        <MenuItem
          onClick={() => {
            navigate("/settings/password");
            handleClose();
            onSideMenuClose?.();
          }}
          sx={
            isDark
              ? {
                  color: "#fff",
                  "&:hover": { bgcolor: "rgba(255,255,255,0.08)" },
                }
              : undefined
          }
        >
          {t("dropdown.changePassword")}
        </MenuItem>
        {/* To go out */}
        <MenuItem
          sx={
            isDark
              ? {
                  color: "#ff6b6b",
                  "&:hover": { bgcolor: "rgba(255,107,107,0.1)" },
                }
              : undefined
          }
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleClose();

            // IMPORTANTE: NÃO usar localStorage.clear() pois limpa o cache de conversas!
            // Limpar apenas dados de autenticação, mantendo cache para melhor UX
            localStorage.removeItem("authUser");
            localStorage.removeItem("token");
            localStorage.removeItem("oauth_state");
            localStorage.removeItem("oauth_connector_id");
            localStorage.removeItem("debugMode");
            localStorage.removeItem("pwa-installed");
            localStorage.removeItem("pwa-install-time");

            // Marcar que o cache pode ser do usuário anterior (será verificado no login)
            const cachedUserId = localStorage.getItem("chatCacheUserId");
            if (cachedUserId) {
              localStorage.setItem("chatCachePreviousUser", cachedUserId);
            }

            // Clear cookies
            document.cookie.split(";").forEach((c) => {
              document.cookie = c
                .replace(/^ +/, "")
                .replace(
                  /=.*/,
                  "=;expires=" + new Date().toUTCString() + ";path=/",
                );
            });

            // Hard redirect - fastest way without React Router delays
            window.location.replace("/auth-login");

            // Dispatch logout in background (non-blocking)
            setTimeout(() => {
              dispatch(logoutUser());
            }, 0);
          }}
        >
          {t("dropdown.logout")}
        </MenuItem>
      </Menu>
    </>
  );
};

export default ProfileDropdownMenu;
